// "What version is the server serving right now?"
//
// `__APP_VERSION__` is baked into the bundle at build time, so it answers what
// THIS TAB is running and can never answer what has been deployed since. This
// reads `version.json`, emitted next to the bundle by the version-manifest
// plugin in `vite.config.ts`, and the gap between the two is the whole signal:
// a tab running 0.1.241 while the server serves 0.1.242 is a tab whose next
// lazy route is going to 404 (see `lazyRoute.ts`).
//
// WHY NOT REUSE M46's CHANGELOG CHECK. `StaleBundleProvider` already compares
// against the newest published `changelog_entries` row, and that is the right
// source for "there are release notes you have not read" — but it is the wrong
// source for this, twice over: the row is written BY HAND after the deploy, so
// there is a window where the code has moved and the changelog has not, and a
// release that never gets a row would be invisible forever. This file asks the
// build itself.
//
// CACHING IS THE ONE TRAP. A `version.json` served from cache would report the
// version this tab already has, forever — the check would be a no-op that looks
// like it is working. Hence `cache: 'no-store'` AND a cache-busting query: the
// header covers the browser's HTTP cache, the query covers anything in front of
// it that ignores the header.
//
// EVERY FAILURE IS SILENT. Offline, a 404 in dev (there is no version.json
// until a build runs), a Netlify blip — none of them are worth a word to the
// user. The next check answers, and until it does the app behaves exactly as it
// did before this existed.

export interface DeployedVersion {
  version: string;
  builtAt?: string;
}

/** The version the server is serving, or null if it cannot be determined. */
export async function fetchDeployedVersion(
  fetchImpl: typeof fetch = fetch,
): Promise<string | null> {
  try {
    const res = await fetchImpl(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { accept: 'application/json' },
    });
    if (!res.ok) return null;
    const body = (await res.json()) as Partial<DeployedVersion> | null;
    const version = typeof body?.version === 'string' ? body.version.trim() : '';
    return version || null;
  } catch {
    return null;
  }
}
